import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  chatHealth,
  newSession,
  sendMessage,
  submitMedOpsLeadForm,
  submitPharmaLeadForm,
  submitQueryForm,
} from "../services/chat";
import { useAuth } from "../contexts/AuthContext";
import UnifiedLeadForm from "./UnifiedLeadForm";
import { ContactForm } from "./ContactForm";
import MedimindLogo from "../assets/Icon MM.png"; // ⬅️ fix path if different

// ───────────────────────────────────────────── tiny utils
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const uid = () =>
  globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : Math.random().toString(36).slice(2);

const escapeHtml = (s) =>
  s.replace(/[&<>"']/g, (m) => {
    const map = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return map[m];
  });

const dayFmt = (ts) =>
  new Date(ts).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
const timeFmt = (ts) =>
  new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// storage keys
const LS_THREADS = "cb_threads_v2";
const LS_ACTIVE = "cb_thread_active_v2";

// ───────────────────────────────────────────── Lead Form Utils
const LEAD_FIELDS = [
  { name: "fullName", label: "Full name", type: "text", required: true },
  { name: "phoneNumber", label: "Phone number", type: "tel", required: true },
  { name: "email", label: "Email", type: "email", required: true },
];

// Open the inline form & (optionally) fetch intro from Rasa
async function openForm(kind, pushBot, doSendSilent) {
  let silentPayload;
  let formFields;

  if (kind === "MEDOPS") {
    silentPayload = "/choose_medops";
    formFields = LEAD_FIELDS;
  } else if (kind === "PHARMA") {
    silentPayload = "/choose_pharma_connect";
    formFields = LEAD_FIELDS; // Removed pharmacy field
  } else {
    // kind === "OTHER"
    silentPayload = "/choose_other";
    formFields = [...LEAD_FIELDS, { name: "query", label: "Your Query", type: "text", required: true }];
  }

  const botResponse = await doSendSilent(silentPayload);

  // If there's a text response from the bot, display it before the form
  if (botResponse && botResponse.length > 0 && botResponse[0].type === "text" && botResponse[0].text) {
    pushBot(botResponse[0].text);
  }

  pushBot(undefined, undefined, undefined, {
    type: "form",
    form: { form: kind, fields: formFields },
  });
}

// ───────────────────────────────────────────── Component
export default function ProChatWidget({
  title = "Medimind",
  welcome = "🙏 Thank you for visiting Medimind, welcome! How can I help you today?",
  position = "right",
  brand = "#2563eb",
  avatarBotUrl,
  avatarMeUrl,
  enableVoice = true,
  enableFiles = true,
  maxThreads = 6,
  initialHeight = 600,
  panelWidth = 400,
}) {
  const { user } = useAuth();

  const chatPanelRef = useRef(null);
  const fabRef = useRef(null);
  const scRef = useRef(null); // Added scRef

  const [dark, setDark] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const on = (e) => setDark(e.matches);
    mq?.addEventListener?.("change", on);
    return () => mq?.removeEventListener?.("change", on);
  }, []);

  const themeVars = useMemo(
    () => ({
      "--cb-brand": brand,
      "--cb-brand-grad": `linear-gradient(135deg, ${brand}, #3b82f6)`,
      "--cb-surface": "#F8F8F8",
      "--cb-panel": "#F8F8F8",
      "--cb-bg": "#F8F8F8",
      "--cb-text": "#0a0a0a",
      "--cb-sub": "#1f2937",
      "--cb-border": dark ? "rgba(255,255,255,.12)" : "#e7e8ee",
      "--cb-bot": dark ? "rgba(255,255,255,.06)" : "#ffffff",
      "--cb-me": dark ? "#0f1115" : "#111316",
      "--cb-code-bg": dark ? "rgba(255,255,255,.08)" : "#f2f4f8",
      "--cb-code-fg": "#0a0a0a",
      "--cb-shadow": dark ? "0 32px 80px rgba(0,0,0,.55)" : "0 32px 80px rgba(16,24,40,.18)",
      "--cb-logo-bg": "#f3f4f6",
    }),
    [dark, brand]
  );

  const [online, setOnline] = useState(false);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const [listening, setListening] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [showContactForm, setShowContactForm] = useState(false);

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const active = useMemo(() => threads.find((t) => t.id === activeId) || null, [threads, activeId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scRef.current) {
      scRef.current.scrollTop = scRef.current.scrollHeight;
    }
  }, [active?.rows]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatPanelRef.current &&
        !chatPanelRef.current.contains(event.target) &&
        fabRef.current &&
        !fabRef.current.contains(event.target) &&
        open
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // health
  useEffect(() => {
    (async () => {
      try {
        await chatHealth();
        setOnline(true);
      } catch (e) {
        console.error("Chat health check failed:", e);
        setOnline(false);
      }
    })();
  }, []);

  // persist
  useEffect(() => {
    localStorage.setItem(LS_THREADS, JSON.stringify(threads));
  }, [threads]);
  useEffect(() => {
    if (activeId) localStorage.setItem(LS_ACTIVE, String(activeId));
  }, [activeId]);

  const bumpThread = useCallback((id, patch) => {
    setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch, updatedAt: Date.now() } : t)));
  }, []);

  // push
  const pushRow = useCallback(
    (r) => {
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.id === activeId);
        if (idx === -1) return prev;
        const thread = prev[idx];
        const unreadCount = r.who === "bot" && !open ? thread.unread + 1 : thread.unread;
        const next = { ...thread, rows: [...thread.rows, r], unread: unreadCount, updatedAt: Date.now() };
        const copy = [...prev];
        copy[idx] = next;
        return copy;
      });
    },
    [activeId, open]
  );

  const pushMe = useCallback((text, meta) => pushRow({ who: "me", text, ts: Date.now(), meta }), [pushRow]);
  const pushBot = useCallback(
    (text, buttons, image, custom) => pushRow({ who: "bot", text, buttons, image, custom, ts: Date.now() }),
    [pushRow]
  );
  const pushSys = useCallback((text) => pushRow({ who: "sys", text, ts: Date.now() }), [pushRow]);

  // reset unread
  useEffect(() => {
    if (open && active && active.unread > 0) bumpThread(active.id, { unread: 0 });
  }, [open, active, bumpThread]);

  // new session on open
  useEffect(() => {
    const handleOpen = async () => {
      if (!open) return;
      localStorage.removeItem(LS_THREADS);
      localStorage.removeItem(LS_ACTIVE);
      setThreads([]);
      setActiveId(null);
      const th = await createThread("New Chat", welcome, false);
      setThreads([th]);
      setActiveId(th.id);
    };
    handleOpen();
  }, [open]); // eslint-disable-line

  async function createThread(title = "New Chat", initialMessage, addToState = false) {
    let sid;
    try {
      sid = await newSession();
    } catch {
      sid = `client_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    }
    const th = {
      id: uid(),
      title,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      sessionId: sid,
      unread: 0,
      rows: initialMessage ? [{ who: "bot", text: initialMessage, ts: Date.now() }] : [],
    };
    if (addToState) setThreads((ts) => [th, ...ts].slice(0, maxThreads));
    return th;
  }

  // Placeholder for file handling
  const handleFiles = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;
      setUploading(true);
      await sleep(1000);
      setUploading(false);
      pushSys("[info] File upload simulated. Actual upload logic needs to be implemented.");
    },
    [pushSys]
  );

  // Placeholder for voice listening
  const toggleListen = useCallback(() => {
    setListening((prev) => !prev);
    pushSys("[info] Voice input simulated. Actual voice recognition logic needs to be implemented.");
  }, [pushSys]);

  // send
  const lastSendRef = useRef(0);
  function rateOK() {
    const now = Date.now();
    if (now - lastSendRef.current < 250) return false;
    lastSendRef.current = now;
    return true;
  }

  const canSend = useCallback(() => draft.trim().length > 0, [draft]);

  // Watch for bot messages that include the contact form to toggle showContactForm
  useEffect(() => {
    if (!active) return;
    const lastBotMessage = [...active.rows].reverse().find((r) => r.who === "bot");
    if (lastBotMessage?.custom?.type === "form" && lastBotMessage.custom.form.form === "CONTACT") {
      setShowContactForm(true);
    } else {
      setShowContactForm(false);
    }
  }, [active?.rows]);

  const doSend = useCallback(
    async (text) => {
      let current = active;
      if (!current) {
        const th = await createThread("New Chat", undefined, true);
        setActiveId(th.id);
        current = th;
        await sleep(50);
      }
      if (!current) return pushSys("[error] chat session unavailable. Please reload.");

      const msg = (text ?? draft).trim();
      if (!msg || !rateOK()) return;

      pushMe(msg);
      setDraft("");
      setTyping(true);

      const metadata = {
        user_id: user?.id ?? user?.employee_id,
        email: user?.email,
        name: user?.name ?? user?.username,
        role: user?.role ?? user?.role_details?.name,
      };

      try {
        const res = await sendMessage({ session_id: current.sessionId, message: msg, metadata });
        const msgs = res?.responses || [];
        if (!msgs.length) pushBot("…no response from bot", undefined, undefined, { sessionId: current.sessionId });

        msgs.forEach((m) => {
          if (m?.text) {
            pushBot(m.text, m.buttons);
            return;
          }
          if (m?.buttons) {
            pushBot("Choose an option:", m.buttons);
            return;
          }
          if (m?.image) {
            pushBot(undefined, undefined, m.image);
            return;
          }
          if (m?.cards) {
            pushBot(undefined, undefined, undefined, m.cards);
            return;
          }
          if (m?.form) {
            pushBot(undefined, undefined, undefined, { type: "form", form: m.form });
            return;
          }

          const c = m?.custom?.custom ?? m?.custom;
          if (c?.type === "form" && c?.form?.fields) {
            pushBot(undefined, undefined, undefined, { type: "form", form: c.form });
            return;
          }

          if (c?.type === "close_form") {
            const filteredRows = current.rows.filter((r) => r.text !== "[custom payload]");
            const lastRow = filteredRows[filteredRows.length - 1];
            if (lastRow) {
              const previewText = `Form submitted:\n${lastRow.text || ""}`;
              pushBot(previewText);
            }
            return;
          }

          pushBot("[custom payload]");
        });
      } catch (e) {
        pushSys(`Error: ${e?.message || e}`);
        bumpThread(current.id, {
          rows: [...current.rows, { who: "sys", text: "[queued offline]", ts: Date.now() }],
        });
      } finally {
        setTyping(false);
      }
    },
    [active, draft, user, pushMe, pushSys, pushBot, rateOK, createThread, setActiveId, bumpThread]
  );

  const doSendSilent = useCallback(
    async (text, payload) => {
      let current = active;
      if (!current) {
        const th = await createThread("New Chat", undefined, true);
        setActiveId(th.id);
        current = th;
        await sleep(50);
      }
      if (!current) {
        pushSys("[error] chat session unavailable. Please reload.");
        return [];
      }

      const msg = (text ?? "").trim();
      if (!msg) return [];

      setTyping(true);

      try {
        let res;

        if (msg === "/medops_submit" && payload) {
          const djangoPayload = {
            full_name: payload.fullName,
            email: payload.email,
            phone: payload.phoneNumber,
            message: payload.message || "",
          };
          await submitMedOpsLeadForm(djangoPayload);
          pushSys("MedOps form submitted successfully!");
          res = await sendMessage({
            session_id: current.sessionId,
            message: msg,
            metadata: { form_submitted: true, payload: payload, name: payload.fullName },
          });
        } else if (msg === "/pharma_submit" && payload) {
          const djangoPayload = {
            full_name: payload.fullName,
            email: payload.email,
            phone: payload.phoneNumber,
            message: payload.message || "",
          };
          await submitPharmaLeadForm(djangoPayload);
          pushSys("Pharma Connect form submitted successfully!");
          res = await sendMessage({
            session_id: current.sessionId,
            message: msg,
            metadata: { form_submitted: true, payload: payload, name: payload.fullName },
          });
        } else if (msg === "/other_submit" && payload) {
          const djangoPayload = {
            name: payload.fullName,
            email: payload.email,
            phone: payload.phoneNumber,
            query: payload.query || "",
          };
          await submitQueryForm(djangoPayload);
          pushSys("Contact form submitted successfully!");
          res = await sendMessage({
            session_id: current.sessionId,
            message: msg,
            metadata: { form_submitted: true, payload: payload, name: payload.fullName },
          });
        } else {
          const metadata = {
            user_id: user?.id ?? user?.employee_id,
            email: user?.email,
            name: user?.name ?? user?.username,
            role: user?.role ?? user?.role_details?.name,
          };
          if (payload) metadata.payload = payload;
          res = await sendMessage({ session_id: current.sessionId, message: msg, metadata });
        }

        const msgs = res?.responses || [];
        if (!msgs.length && res) pushBot("…no response from bot", undefined, undefined, { sessionId: current.sessionId });

        msgs.forEach((m) => {
          if (m?.text) return pushBot(m.text, m.buttons);
          if (m?.buttons) return pushBot("Choose an option:", m.buttons);
          if (m?.image) return pushBot(undefined, undefined, m.image);
          if (m?.cards) return pushBot(undefined, undefined, undefined, m.cards);
          if (m?.form) return pushBot(undefined, undefined, undefined, { type: "form", form: m.form });
          const c = m?.custom?.custom ?? m?.custom;
          if (c?.type === "form" && c?.form?.fields) return pushBot(undefined, undefined, undefined, { type: "form", form: c.form });
          pushBot("[custom payload]");
        });

        return msgs;
      } catch (e) {
        pushSys(`Error: ${e?.message || e}`);
        return [];
      } finally {
        setTyping(false);
      }
    },
    [active, user, pushSys, pushBot, createThread, setActiveId]
  );

  const doSendRef = useRef(undefined);
  useEffect(() => {
    doSendRef.current = doSend;
  }, [doSend]);

  useEffect(() => {
    const handler = (e) => doSendRef.current?.(e.detail);
    document.addEventListener("cb-send", handler);
    return () => document.removeEventListener("cb-send", handler);
  }, []);

  const needsDay = (rows, i) =>
    i === 0 || new Date(rows[i - 1].ts).toDateString() !== new Date(rows[i].ts).toDateString();

  const botAvatar = avatarBotUrl || MedimindLogo;

  // ───────────────────────────────────────────── UI
  return (
    <>
      {/* "Let's connect" animated message */}
      {!open && (
        <div
          style={{
            position: "fixed",
            [position]: 95,
            bottom: 35,
            background: "linear-gradient(135deg, #2563eb, #3b82f6)",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 20,
            fontSize: 14,
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(37, 99, 235, 0.3)",
            zIndex: 9999998,
            whiteSpace: "nowrap",
            animation: "cb-slideIn 0.6s ease-out, cb-pulse 2s infinite",
            pointerEvents: "none",
            ...themeVars,
          }}
        >
          👋 Let's connect
        </div>
      )}

      {/* floating action button */}
      <button
        ref={fabRef}
        aria-label="Chat"
        onClick={() => setOpen(!open)}
        title={online ? "Chat" : "Bot offline"}
        style={{
          position: "fixed",
          [position]: 20,
          bottom: 20,
          width: 62,
          height: 62,
          borderRadius: 999,
          border: "none",
          background: `#D3D3D3`,
          color: "#fff",
          boxShadow: "var(--cb-shadow)",
          cursor: "pointer",
          zIndex: 9999999,
          display: "grid",
          placeItems: "center",
          transform: `translateZ(0)`,
          transition: "transform .15s ease",
          animation: !open ? "cb-jump 0.8s ease-in-out infinite" : "none",
          ...themeVars,
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(.96)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {open ? (
          <span style={{ fontSize: 22 }}>✖️</span>
        ) : (
          <img src={botAvatar} alt="Medimind" style={{ width: 30, height: 30, objectFit: "contain" }} />
        )}
        {!!unread && !open && (
          <span
            aria-label={`${unread} unread`}
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              minWidth: 22,
              height: 22,
              borderRadius: 11,
              background: "#ff3b3b",
              color: "#fff",
              fontSize: 12,
              fontWeight: 800,
              display: "grid",
              placeItems: "center",
              padding: "0 6px",
              boxShadow: "var(--cb-shadow)",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* panel */}
      <div
        ref={chatPanelRef}
        role="dialog"
        aria-label="Chat dialog"
        style={{
          position: "fixed",
          [position]: 20,
          bottom: 90,
          width: panelWidth,
          maxWidth: "98vw",
          height: open ? initialHeight : 0,
          maxHeight: "80vh",
          borderRadius: 18,
          overflow: "hidden",
          background: "var(--cb-surface)",
          border: "1px solid var(--cb-border)",
          boxShadow: "var(--cb-shadow)",
          transform: `translateY(${open ? "0" : "14px"}) scale(${open ? 1 : 0.98})`,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .18s ease, transform .18s ease, height .18s ease",
          zIndex: 9999999,
          display: "flex",
          flexDirection: "column",
          ...themeVars,
        }}
      >
        {/* header */}
        <div
          style={{
            background: "var(--cb-brand-grad)",
            color: "#fff",
            padding: "10px 12px",
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            alignItems: "center",
            gap: 10,
            fontWeight: 800,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 12,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                background: "var(--cb-logo-bg)",
              }}
            >
              <img src={botAvatar} alt="Medimind" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }} />
            </div>
            <div style={{ fontWeight: 900 }}>{title}</div>
          </div>

          {/* thread switcher */}
          <div style={{ display: "flex", gap: 6, overflowX: "auto", justifyContent: "flex-end" }}>
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setActiveId(t.id);
                  setUnread(0);
                }}
                title={t.title}
                style={{
                  whiteSpace: "nowrap",
                  border: "none",
                  borderRadius: 999,
                  padding: "6px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: t.id === activeId ? "rgba(255,255,255,.3)" : "rgba(255,255,255,.18)",
                  color: "#fff",
                }}
              >
                {t.title}
                {t.unread ? ` • ${t.unread}` : ""}
              </button>
            ))}
          </div>
        </div>

        {!online && (
          <div
            style={{
              background: dark ? "rgba(255,215,0,.1)" : "#fff8db",
              color: dark ? "#ffe27a" : "#7a5f00",
              padding: "8px 12px",
              fontSize: 12,
              borderBottom: "1px solid var(--cb-border)",
            }}
          >
            Bot seems offline — messages will try, queued otherwise.
          </div>
        )}

        {/* messages */}
        <div
          ref={scRef}
          onDragOver={(e) => {
            if (enableFiles) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onDrop={(e) => {
            if (enableFiles) {
              e.preventDefault();
              handleFiles(e.dataTransfer.files);
            }
          }}
          style={{ flex: "1 1 0%", overflow: "auto", background: "var(--cb-bg)", padding: 12, ...themeVars }}
        >
          {active?.rows.map((r, i, arr) => (
            <React.Fragment key={`${r.ts}-${i}`}>
              {needsDay(arr, i) && (
                <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--cb-sub)",
                      background: dark ? "rgba(255,255,255,.08)" : "#fff",
                      border: "1px solid var(--cb-border)",
                      padding: "2px 8px",
                      borderRadius: 999,
                    }}
                  >
                    {dayFmt(r.ts)}
                  </div>
                </div>
              )}

              <Bubble
                r={r}
                grouped={i > 0 && r.who === arr[i - 1].who && r.ts - arr[i - 1].ts < 4 * 60 * 1000}
                avatarBotUrl={botAvatar}
                avatarMeUrl={avatarMeUrl}
                dark={dark}
                active={active}
                pushBot={pushBot}
                doSend={doSend}
                doSendSilent={doSendSilent}
                bumpThread={bumpThread}
                setThreads={setThreads}
              />
            </React.Fragment>
          ))}

          {typing && (
            <div style={{ display: "flex", justifyContent: "flex-start", margin: "8px 0" }}>
              <div
                style={{
                  maxWidth: "60%",
                  padding: "10px 12px",
                  borderRadius: 14,
                  background: "var(--cb-bot)",
                  border: "1px solid var(--cb-border)",
                }}
              >
                <TypingDots />
              </div>
            </div>
          )}
        </div>

        {/* composer */}
        <div style={{ background: "var(--cb-panel)", borderTop: "1px solid var(--cb-border)", padding: 10, ...themeVars }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <textarea
              placeholder="Type a message…"
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  doSend(undefined);
                }
              }}
              style={{
                flex: 1,
                resize: "none",
                border: "1px solid var(--cb-border)",
                outline: "none",
                padding: "10px 12px",
                fontSize: 14,
                lineHeight: 1.35,
                borderRadius: 12,
                background: dark ? "rgba(255,255,255,.06)" : "#fff",
                color: "var(--cb-text)",
              }}
            />
            <button onClick={() => doSend(undefined)} disabled={!canSend()} style={{ ...sendBtn, opacity: canSend() ? 1 : 0.5 }}>
              Send
            </button>
            {enableVoice && (
              <button onClick={toggleListen} title="Voice" style={iconBtn}>
                {listening ? "🎙️" : "🎤"}
              </button>
            )}
            {enableFiles && (
              <label title="Attach files" style={{ ...iconBtn, display: "grid", placeItems: "center" }}>
                📎
                <input type="file" multiple onChange={(e) => handleFiles(e.target.files)} style={{ display: "none" }} />
              </label>
            )}
          </div>
          {uploading && <div style={{ fontSize: 12, color: "var(--cb-sub)", marginTop: 6 }}>Uploading…</div>}
        </div>
      </div>

      <style>{`
        @keyframes cb-in { from { transform: translateY(6px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        @keyframes cb-dots { 0%,80%,100%{transform:scale(.7);opacity:.35} 40%{transform:scale(1);opacity:.95} }
        @keyframes cb-jump { 0%,100% { transform: translateY(0) } 25% { transform: translateY(-5px) } 50% { transform: translateY(0) } 75% { transform: translateY(-2px) } }
        @keyframes cb-connect { 
          0% { transform: scale(0.95) translateY(8px); opacity: 0; }
          50% { transform: scale(1.02) translateY(-2px); }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes cb-pulse { 
          0%, 100% { box-shadow: 0 8px 24px rgba(37, 99, 235, 0.3); }
          50% { box-shadow: 0 8px 32px rgba(37, 99, 235, 0.5); }
        }
        @keyframes cb-slideIn {
          0% { 
            transform: translateX(-20px); 
            opacity: 0; 
          }
          100% { 
            transform: translateX(0); 
            opacity: 1; 
          }
        }
      `}</style>
    </>
  );
}

// ───────────────────────────────────────────── Subcomponents + styles
function renderMarkdown(text) {
  let out = escapeHtml(text);
  out = out
    .replace(
      /```([\s\S]*?)```/g,
      (_m, code) =>
        `<pre style="margin:6px 0;padding:8px;border-radius:8px;background:var(--cb-code-bg);color:var(--cb-code-fg);overflow:auto">${escapeHtml(
          code
        )}</pre>`
    )
    .replace(
      /`([^`]+)`/g,
      (_m, code) =>
        `<code style="padding:2px 5px;border-radius:6px;background:var(--cb-code-bg);color:var(--cb-code-fg)">${escapeHtml(
          code
        )}</code>`
    )
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/(https?:\/\/[^\s)]+)(?=\)|\s|$)/g, '<a href="$1" target="_blank" rel="noreferrer">$1</a>');
  return out;
}

const iconBtn = {
  border: "none",
  background: "rgba(255,255,255,.22)",
  color: "#fff",
  borderRadius: 8,
  padding: "6px 8px",
  cursor: "pointer",
  fontWeight: 800,
};

const chipBtn = {
  border: "1px solid var(--cb-border)",
  background: "transparent",
  color: "var(--cb-text)",
  borderRadius: 999,
  padding: "6px 12px",
  fontSize: 12,
  cursor: "pointer",
};

const sendBtn = {
  border: "none",
  background: "var(--cb-brand-grad)",
  color: "#fff",
  padding: "10px 14px",
  fontWeight: 800,
  borderRadius: 12,
  boxShadow: "var(--cb-shadow)",
};

function TypingDots() {
  return (
    <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: 6,
            background: "var(--cb-sub)",
            opacity: 0.6,
            animation: "cb-dots 1.2s infinite ease-in-out",
            animationDelay: `${i * 0.12}s`,
          }}
        />
      ))}
    </div>
  );
}

function Bubble({
  r,
  grouped,
  avatarBotUrl,
  avatarMeUrl,
  dark,
  active,
  pushBot,
  doSend,
  doSendSilent,
  bumpThread,
  setThreads,
}) {
  if (r.who === "sys") {
    return (
      <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
        <div style={{ fontSize: 12, color: "var(--cb-sub)" }}>{r.text}</div>
      </div>
    );
  }

  const isMe = r.who === "me";
  const isForm = r.custom?.type === "form" && Array.isArray(r.custom?.form?.fields);

  const bubble = {
    maxWidth: isForm ? "min(260px, 92vw)" : "78%",
    width: isForm ? "min(260px, 92vw)" : undefined,

    padding: "10px 12px",
    borderRadius: 14,
    lineHeight: 1.35,
    fontSize: 14,
    background: r.who === "me" ? "var(--cb-me)" : "var(--cb-bot)",
    color: r.who === "me" ? "#fff" : "var(--cb-text)",
    border: r.who === "me" ? "none" : "1px solid var(--cb-border)",
    borderTopRightRadius: r.who === "me" ? 6 : 14,
    borderTopLeftRadius: r.who === "me" ? 14 : 6,
    wordBreak: "break-word",
    animation: grouped ? undefined : r.who === "bot" ? "cb-connect .5s ease-out" : "cb-in .18s ease",
    boxShadow: r.who === "me" ? "none" : dark ? "0 4px 14px rgba(0,0,0,.25)" : "0 4px 14px rgba(16,24,40,.08)",

    overflow: isForm ? "visible" : "auto",
    maxHeight: isForm ? "300px" : "60vh",
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isMe ? "flex-end" : "flex-start",
        margin: grouped ? "2px 0" : "10px 0",
        gap: 8,
      }}
    >
      {!isMe && !grouped && (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 12,
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
            background: "var(--cb-logo-bg)",
          }}
        >
          {avatarBotUrl ? (
            <img src={avatarBotUrl} alt="Medimind" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <span>🤖</span>
          )}
        </div>
      )}

      <div style={bubble}>
        {r.image && (
          <img
            src={r.image}
            alt=""
            style={{ maxWidth: 260, maxHeight: 260, borderRadius: 10, marginBottom: r.text ? 8 : 0 }}
          />
        )}

        {/* form payload */}
        {isForm && (
          <div style={{ marginTop: 6, width: "100%", maxWidth: 220 }}>
            {(() => {
              const metaForm = r.custom?.form?.form || "MEDOPS";
              const base = r.custom?.form?.fields || LEAD_FIELDS;
              const fields = base;

              const normalized = fields.map((f) =>
                f.name === "phoneNumber" ? { ...f, type: "tel", required: true } : f
              );

              const intent =
                metaForm === "PHARMA" ? "/pharma_submit" : metaForm === "OTHER" ? "/other_submit" : "/medops_submit";

              const closeThisForm = () => {
                setThreads((prev) => {
                  const idx = prev.findIndex((t) => t.id === active?.id);
                  if (idx === -1) return prev;
                  const t = prev[idx];
                  const rows = [...t.rows];
                  for (let i = rows.length - 1; i >= 0; i--) {
                    if (rows[i].custom?.type === "form") {
                      rows.splice(i, 1);
                      break;
                    }
                  }
                  const copy = [...prev];
                  copy[idx] = { ...t, rows, updatedAt: Date.now() };
                  return copy;
                });
              };

              return (
                <UnifiedLeadForm
                  sessionId={active?.sessionId || ""}
                  intent={intent}
                  fields={normalized}
                  onFormSubmit={closeThisForm}
                  onFormSubmitIntent={async (message, payload) => {
                    await doSendSilent(message, payload);
                  }}
                />
              );
            })()}
          </div>
        )}

        {/* text */}
        {r.text && <div dangerouslySetInnerHTML={{ __html: renderMarkdown(r.text) }} />}

        {/* buttons */}
        {!!r.buttons?.length && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            {r.buttons.map((b, idx) => (
              <button
                key={idx}
                onClick={async () => {
                  const p = b.payload || b.title;

                  if (p === "/choose_medops") {
                    await openForm("MEDOPS", pushBot, doSendSilent);
                    return;
                  }
                  if (p === "/choose_pharma_connect") {
                    await openForm("PHARMA", pushBot, doSendSilent);
                    return;
                  }
                  if (p === "/choose_other") {
                    await openForm("OTHER", pushBot, doSendSilent);
                    return;
                  }

                  document.dispatchEvent(new CustomEvent("cb-send", { detail: p }));
                }}
                style={{
                  border: "1px solid #ccc",
                  background: "#D0E0E3",
                  color: "var(--cb-text)",
                  borderRadius: 999,
                  padding: "7px 12px",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {b.title}
              </button>
            ))}
          </div>
        )}

        <div style={{ marginTop: 4, fontSize: 11, color: "var(--cb-sub)", textAlign: isMe ? "right" : "left" }}>
          {timeFmt(r.ts)}
        </div>
      </div>

      {isMe && !grouped && (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 12,
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
            background: dark ? "rgba(255,255,255,.08)" : "#eef1f6",
          }}
        >
          {avatarMeUrl ? (
            <img src={avatarMeUrl} alt="me" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span>🧑</span>
          )}
        </div>
      )}
    </div>
  );
}